import { useMutation, useQuery } from '@tanstack/react-query'
import { useContext } from 'react'
import AppContext from '../../context/appContext'
import createEligibilityCheck from '../../services/ruutsApi/createEligibilityCheck'
import {
  type EligibilityCheckMeta,
  type EligibilityCheckQuery,
  getEligibilityCheckWithMeta,
} from '../../services/ruutsApi/getEligibilityCheck'
import type { Paginated } from '../../services/ruutsApi/normalize'
import type { EligibilityArea } from '../../types/apiModels'
import { isEligibilityEnabled } from '../../utils/programConfig/areaSubtractions'
import { queryKeys } from './queryKeys'

export type { EligibilityCheckQuery }

// Single source of truth shared by every eligibility consumer (accordion context,
// attestation report, sample planning); a module-level reference also keeps the
// query key stable across renders.
export const ELIGIBILITY_QUERY: EligibilityCheckQuery = {
  filterAreas: ['forest', 'otbnRedAreas'],
}

export const ELIGIBILITY_POLL_INTERVAL_MS = 30_000

// The backend is the source of truth via `meta.hasEligibilityCheck`, but a check that ran and
// found areas is unambiguous proof on its own — shared here so the returned flag and the poll
// predicate can never disagree if `meta` is ever missing from a response.
const resolveHasEligibilityCheck = (
  data?: Paginated<EligibilityArea, EligibilityCheckMeta>,
): boolean => data?.meta?.hasEligibilityCheck === true || (data?.items?.length ?? 0) > 0

export const useEligibilityCheck = ({
  farmId,
  query,
  isCheckRunning = false,
}: {
  farmId: string | undefined
  query?: EligibilityCheckQuery
  isCheckRunning?: boolean
}) => {
  const { programConfig } = useContext(AppContext)
  const eligibilityEnabled = isEligibilityEnabled(programConfig)
  const result = useQuery({
    queryKey: queryKeys.eligibilityCheck(farmId, query),
    queryFn: ({ signal }) =>
      getEligibilityCheckWithMeta({ farmId: farmId as string, query, signal }),
    enabled: !!farmId && eligibilityEnabled,
    // The detection is a deferred backend job with no status endpoint and `hasEligibilityCheck` stays
    // false until it persists its rows, so polling is the only way the result shows up without a reload.
    refetchInterval: ({ state }) =>
      isCheckRunning && !resolveHasEligibilityCheck(state.data)
        ? ELIGIBILITY_POLL_INTERVAL_MS
        : false,
  })

  const createMutation = useMutation({
    mutationFn: () => {
      if (!farmId) {
        throw new Error('Farm ID is required to create an eligibility check.')
      }
      return createEligibilityCheck({ farmId })
    },
    // The accordion shows its own error toast.
    meta: { suppressGlobalError: true },
  })

  return {
    eligibilityCheckAreas: result.data?.items ?? null,
    hasEligibilityCheck: resolveHasEligibilityCheck(result.data),
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetchEligibilityCheck: result.refetch,
    isFetchingEligibilityCheck: result.isFetching,
    createEligibilityCheck: createMutation.mutateAsync,
  }
}
